import React, { createContext, ReactNode, useContext } from 'react';
import { getCurrentUser } from '../util/auth';
import { User, useGetUser } from './api/user';

const CURRENT_USER_REFETCH_INTERVAL = 30000;

interface CurrentUserContextValue {
  currentUserId?: number;
  isLoggedIn: boolean;
  isInstructor: boolean;
  isLoadingCurrentUser: boolean;
  isError: boolean;
  user?: User;
}

const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(undefined);

function useCurrentUserQuery(): CurrentUserContextValue {
  const tokenUser = getCurrentUser();
  const parsedUserId = tokenUser?.sub ? Number(tokenUser.sub) : undefined;
  const currentUserId = Number.isNaN(parsedUserId) ? undefined : parsedUserId;
  const userQuery = useGetUser(currentUserId, {
    refetchInterval: CURRENT_USER_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
  });
  const isLoggedIn = currentUserId !== undefined;

  return {
    currentUserId,
    isLoggedIn,
    isInstructor: userQuery.data?.instructor === true,
    user: userQuery.data,
    isError: userQuery.isError,
    isLoadingCurrentUser: isLoggedIn && userQuery.isLoading,
  };
}

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const currentUser = useCurrentUserQuery();

  return <CurrentUserContext.Provider value={currentUser}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser() {
  const currentUser = useContext(CurrentUserContext);

  if (!currentUser) {
    throw new Error('useCurrentUser must be used within CurrentUserProvider');
  }

  return currentUser;
}
